// src/components/Prompt Library/PromptLibrary.js
import React, { useState, useEffect } from 'react';
import {
  Container,
  Header,
  Cards,
  SpaceBetween,
  Button,
  Modal,
  TextFilter,
  Grid,
  Box,
  Select,
  ColumnLayout,
  FormField,
} from '@cloudscape-design/components';
import PromptForm from '../Chat AI/PromptForm';
import { promptsApi } from '../../services/PromptsApi';

function PromptLibrary() {
  const [prompts, setPrompts] = useState([]);
  const [filteredPrompts, setFilteredPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [filterText, setFilterText] = useState('');
  const [selectedType, setSelectedType] = useState(null);

  const promptTypes = [
    { label: 'All Types', value: '' },
    { label: 'General', value: 'general' },
    { label: 'Cost Management', value: 'cost management' },
    { label: 'Security', value: 'security' },
    { label: 'Performance', value: 'performance' },
    { label: 'Architecture', value: 'architecture' },
    { label: 'Compliance', value: 'compliance' },
    { label: 'Testing', value: 'testing' },
  ];

  const filterPrompts = () => {
    let filtered = prompts;
    
    if (filterText) {
      filtered = filtered.filter(prompt => 
        prompt.title.toLowerCase().includes(filterText.toLowerCase()) ||
        prompt.promptContext.toLowerCase().includes(filterText.toLowerCase())
      );
    }

    if (selectedType?.value) {
      filtered = filtered.filter(prompt => 
        prompt.category.toLowerCase() === selectedType.value.toLowerCase()
      );
    }

    setFilteredPrompts(filtered);
  };

  useEffect(() => {
    fetchPrompts();
  }, []);

  useEffect(() => {
    filterPrompts();
  }, [filterPrompts]);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const fetchedPrompts = await promptsApi.getPrompts();
      setPrompts(fetchedPrompts);
      setFilteredPrompts(fetchedPrompts);
    } catch (error) {
      console.error('Failed to fetch prompts:', error);
    } finally {
      setLoading(false);
    }
  };



  const handleEditPrompt = async (promptData) => {
    try {
      setLoading(true);
      await promptsApi.updatePrompt(promptData);
      await fetchPrompts(); // Refresh the prompts list
      setShowForm(false);
      setSelectedPrompt(null);
    } catch (error) {
      console.error('Failed to update prompt:', error);
      alert('Failed to update prompt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrompt = async (promptData) => {
    try {
      setLoading(true);
      await promptsApi.createPrompt(promptData);
      await fetchPrompts(); // Refresh the prompts list
      setShowForm(false);
    } catch (error) {
      console.error('Failed to create prompt:', error);
      alert('Failed to create prompt. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePrompt = async (promptId) => {
    if (window.confirm('Are you sure you want to delete this prompt?')) {
      try {
        setLoading(true);
        await promptsApi.deletePrompt(promptId);
        await fetchPrompts(); // Refresh the prompts list
      } catch (error) {
        console.error('Failed to delete prompt:', error);
        alert('Failed to delete prompt. Please try again.');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = (promptData) => {
    if (selectedPrompt) {
      handleEditPrompt({ ...promptData, id: selectedPrompt.id });
    } else {
      handleCreatePrompt(promptData);
    }
  };

  return (
    <Container
      header={
        <Header
          variant="h1"
          actions={
            <Button variant="primary" onClick={() => setShowForm(true)}>
              Add New Prompt
            </Button>
          }
        >
          Prompt Library
        </Header>
      }
    >
      <SpaceBetween size="l">
        <Grid gridDefinition={[{ colspan: 12 }]}>
          <SpaceBetween size="l">
            <ColumnLayout columns={2}>
              <FormField label="Search">
                <TextFilter
                  filteringText={filterText}
                  onChange={({ detail }) => setFilterText(detail.filteringText)}
                  placeholder="Search prompts"
                  countText={`${filteredPrompts.length} matches`}
                />
              </FormField>
              <FormField label="Type">
                <Select
                  selectedOption={selectedType}
                  onChange={({ detail }) => setSelectedType(detail.selectedOption)}
                  options={promptTypes}
                  placeholder="Select prompt type"
                />
              </FormField>
            </ColumnLayout>
          </SpaceBetween>
        </Grid>

        <Cards
          loading={loading}
          items={filteredPrompts}
          cardDefinition={{
            header: item => (
              <SpaceBetween size="s">
                <Box variant="h4">{item.title}</Box>
                <Box color="text-body-secondary">{item.category}</Box>
              </SpaceBetween>
            ),
            sections: [
              {
                id: 'promptContext',
                header: 'Prompt',
                content: item => (
                  <Box color="text-body-secondary">
                    {item.promptContext.length > 200 
                      ? `${item.promptContext.substring(0, 200)}...` 
                      : item.promptContext}
                  </Box>
                )
              },
              {
                id: 'actions',
                content: item => (
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button 
                      onClick={() => {
                        setSelectedPrompt(item);
                        setShowForm(true);
                      }}
                    >
                      Edit
                    </Button>
                    <Button 
                      variant="link" 
                      onClick={() => handleDeletePrompt(item.id)}
                    >
                      Delete
                    </Button>
                  </SpaceBetween>
                )
              }
            ]
          }}
          cardsPerRow={[
            { cards: 1, minWidth: 0 },
            { cards: 2, minWidth: 500 },
            { cards: 3, minWidth: 1000 }
          ]}
          empty={
            <Box textAlign="center" color="inherit">
              <b>No prompts</b>
              <Box padding={{ bottom: "s" }}>
                No prompts to display.
              </Box>
              <Button onClick={() => setShowForm(true)}>Add New Prompt</Button>
            </Box>
          }
        />
      </SpaceBetween>

      {showForm && (
        <Modal
          visible={true}
          onDismiss={() => {
            setShowForm(false);
            setSelectedPrompt(null);
          }}
          header={selectedPrompt ? "Edit Prompt" : "Add New Prompt"}
          size="large"
        >
          <PromptForm
            prompt={selectedPrompt}
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setSelectedPrompt(null);
            }}
          />
        </Modal>
      )}
    </Container>
  );
}

export default PromptLibrary;